<script setup lang="ts">
import AmbassadorFlare from '@/components/common/AmbassadorFlare.vue'
import { useRouter } from 'vue-router'
import BaseBadge from '@/components/common/BaseBadge.vue'
import BaseCard from '@/components/common/BaseCard.vue'
import UIChip from '@/components/ui/UIChip.vue'
import { PhPi } from '@phosphor-icons/vue'
import ListingImage from '@/components/listings/ListingImage.vue'
import type { ListingSummary } from '@/types/api'

interface Props {
  listing: ListingSummary
}

const props = defineProps<Props>()

const router = useRouter()

function viewStorefront(event: MouseEvent) {
  event.stopPropagation()
  router.push(`/u/${encodeURIComponent(props.listing.seller.username)}/`)
}

const formatPrice = (price: number) => {
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const getStatusVariant = (status: string) => {
  switch (status) {
    case 'paused': return 'warning'
    case 'completed': return 'success'
    case 'disputed': return 'danger'
    default: return 'gray'
  }
}
</script>

<template>
  <BaseCard
    padding="none"
    class="group bg-surface/50 border border-line rounded-2xl overflow-hidden h-full flex flex-col transition-all hover:border-line-2 hover:shadow-[0_20px_40px_rgb(0,0,0/0.3)] cursor-pointer"
    @click="() => router.push(`/listing/${props.listing.id}/`)"
  >
    <!-- Image -->
    <ListingImage :src="props.listing.images[0]" :alt="props.listing.title" zoom>
      <!-- Status badge: public lists only show active listings, so only flag the others -->
      <BaseBadge
        v-if="props.listing.status !== 'active'"
        :variant="getStatusVariant(props.listing.status)"
        class="absolute top-3 right-3 text-xs"
      >
        {{ props.listing.status }}
      </BaseBadge>
      
      <!-- Pi price badge -->
      <div class="absolute bottom-3 left-3 bg-black/80 backdrop-blur px-3 py-1.5 rounded-xl flex items-center gap-2">
        <PhPi class="w-5 h-5 text-amber-400" />
        <span class="font-bold text-white">{{ formatPrice(props.listing.pricePi) }}</span>
      </div>
    </ListingImage>
    
    <!-- Content -->
    <div class="p-4 flex-1 flex flex-col">
      <!-- Category -->
      <div class="flex items-center gap-2 mb-2">
        <UIChip :label="props.listing.category.name" variant="outline" size="small" />
        <span class="text-xs text-ink-6">{{ props.listing.deliveryDays }}d delivery</span>
        <span v-if="props.listing.salesCount > 0" class="text-xs text-ink-6">· {{ props.listing.salesCount }} sold</span>
      </div>
      
      <!-- Title -->
      <h3 class="font-semibold text-ink mb-1 line-clamp-2">
        {{ props.listing.title }}
      </h3>
      
      <!-- Seller -->
      <button
        type="button"
        class="mt-3 flex w-full items-center gap-2 border-t border-line pt-3 text-left"
        :aria-label="`View ${props.listing.seller.username}'s storefront`"
        @click="viewStorefront"
      >
        <div class="w-8 h-8 rounded-full bg-surface-2 flex items-center justify-center text-sm font-medium text-ink">
          {{ props.listing.seller.username.charAt(0).toUpperCase() }}
        </div>
        <div class="flex-1 min-w-0">
          <p class="flex items-center gap-1 text-sm font-medium text-ink">
            <span class="truncate">{{ props.listing.seller.username }}</span>
            <AmbassadorFlare v-if="props.listing.seller.isAmbassador" />
          </p>
          <div class="flex items-center gap-1">
            <span class="w-3 h-3 text-accent-amber">★</span>
            <span class="text-xs text-ink-5">{{ props.listing.seller.ratingAvg.toFixed(1) }}</span>
            <span class="text-xs text-ink-6">({{ props.listing.seller.ratingCount }})</span>
          </div>
        </div>
      </button>
    </div>
  </BaseCard>
</template>
